// YtreChannelPicker.jsx — Sep 16 2026 (Randy pt-ytre-quick-switch).
//
// "I see no option to quick connect to the 'other' auth youtube account…
//  did you say you simplified this process? now one must have to pop the
//  re-auth every time?"
//
// He was right, for two reasons. The instant switcher shipped on Sep 15 was
// wired to exactly ONE entry point — the Load button on the activity banner —
// so it was absent from all three places he actually looked: the wrong-hat
// banner, the "Connected to X" footer card, and the setup modal. And the
// instant path needs both hats on file, which only happens after a
// successful consent — which the stale-auth-URL bug was blocking. The
// feature was real but sitting behind a jammed door.
//
// This is that one control, reused everywhere: every hat on file, each
// switching with no Google round-trip, plus a way out to a brand-new one.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CaretDown, Check, Plus } from "@phosphor-icons/react";
import { ytApi } from "@/lib/ytApi";

export const YtreChannelPicker = ({
  siloId,
  channelSlot = "channel1",
  connectedChannelId,
  onSwitched,
  onConnectNew,
  label = "Switch channel",
  testid = "ytre-channel-picker",
}) => {
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open || channels) return;
    ytApi.myChannels()
      .then(setChannels)
      .catch(() => setChannels([]));
  }, [open, channels]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = async (c) => {
    if (c.youtube_channel_id === connectedChannelId) { setOpen(false); return; }
    if (!c.can_switch) {
      setOpen(false);
      toast.info(`We hold no sign-in for ${c.youtube_channel_name} yet — connect it once and it's instant after that.`);
      onConnectNew && onConnectNew();
      return;
    }
    setBusyId(c.youtube_channel_id);
    try {
      await ytApi.switchChannel(siloId, channelSlot, c.youtube_channel_id);
      toast.success(`Now posting as ${c.youtube_channel_name}.`);
      setOpen(false);
      onSwitched && onSwitched(c);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't switch channel.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="relative inline-block" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[#2F6B3A] hover:text-[#245029] underline decoration-dotted underline-offset-2"
      >
        {label}
        <CaretDown size={11} weight="bold" className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1.5 z-50 min-w-[240px] bg-white border hairline rounded-lg shadow-xl overflow-hidden"
          data-testid={`${testid}-menu`}
        >
          {channels === null ? (
            <div className="px-3 py-2.5 text-[11.5px] text-[#6B6A68] italic">Loading your channels…</div>
          ) : channels.length === 0 ? (
            <div className="px-3 py-2.5 text-[11.5px] text-[#6B6A68] italic">No channels on file yet.</div>
          ) : (
            channels.map((c) => {
              const isCurrent = c.youtube_channel_id === connectedChannelId;
              return (
                <button
                  key={c.youtube_channel_id}
                  type="button"
                  onClick={() => pick(c)}
                  disabled={!!busyId}
                  data-testid={`${testid}-option-${c.youtube_channel_id}`}
                  className={`w-full text-left px-3 py-2 text-[12px] flex items-center gap-2 hover:bg-[#F4F8F5] disabled:opacity-50 ${
                    isCurrent ? "bg-[#EAF3EC]" : ""
                  }`}
                >
                  <span className="w-3.5 shrink-0">
                    {isCurrent ? <Check size={12} weight="bold" className="text-[#2F6B3A]" /> : null}
                  </span>
                  <span className="flex-1 truncate">
                    {c.youtube_channel_name || c.youtube_channel_id}
                    {!c.can_switch && (
                      <span className="ml-1.5 text-[10px] text-[#8A6A00]">needs sign-in</span>
                    )}
                  </span>
                  {busyId === c.youtube_channel_id && (
                    <span className="text-[10px] text-[#6B6A68]">switching…</span>
                  )}
                </button>
              );
            })
          )}
          {onConnectNew && (
            <button
              type="button"
              onClick={() => { setOpen(false); onConnectNew(); }}
              data-testid={`${testid}-connect-new`}
              className="w-full text-left px-3 py-2 text-[11.5px] flex items-center gap-2 border-t hairline text-[#6B4838] hover:bg-[#F4F8F5]"
            >
              <Plus size={11} weight="bold" />
              Connect a different channel…
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default YtreChannelPicker;
